import { IconButton } from "@/components/ui/buttons/IconButton";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { useOrganizations, useEndpointGroupAccess } from "@/hooks/data";
import type { IOrganization, IEndpointGroup } from "@/services";
import { Plus, X } from "lucide-react";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./EndpointGroupsSection.module.css";
import type { EndpointGroupsSectionProps } from "./types";
import SectionHeader from "@/components/layout/SectionHeader";

/**
 * EndpointGroupsSection - Manage endpoint group access per organization
 * Part of the Superadmin page
 */
export const EndpointGroupsSection: React.FC<EndpointGroupsSectionProps> = ({
  className,
  "data-testid": testId = "endpoint-groups-section",
}) => {
  const { activeOrganizations, isLoading: orgsLoading } = useOrganizations();
  const {
    endpointGroups,
    isLoading: groupsLoading,
    error,
    hasAccess,
    addAccess,
    removeAccess,
    isUpdating,
  } = useEndpointGroupAccess();

  // Selected organization
  const [selectedOrg, setSelectedOrg] = useState<SelectOption<IOrganization>[]>(
    [],
  );

  // Flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLoading = orgsLoading || groupsLoading;
  const selectedOrgId = selectedOrg[0]?.data?.id ?? null;

  // Convert organizations to select options
  const orgOptions: SelectOption<IOrganization>[] = useMemo(() => {
    return activeOrganizations.map((org) => ({
      id: org.id,
      label: `${org.tenant_name} (${org.tenant_id})`,
      data: org,
    }));
  }, [activeOrganizations]);

  // Render org option with tenant ID
  const renderOrgOption = useCallback(
    (option: SelectOption<IOrganization>) => (
      <div
        style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}
      >
        <span style={{ fontWeight: 500 }}>
          {option.data?.tenant_name ?? option.label}
        </span>
        {option.data?.tenant_id && (
          <span
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-secondary, #666)",
              fontFamily: "monospace",
            }}
          >
            {option.data.tenant_id}
          </span>
        )}
      </div>
    ),
    [],
  );

  // Handle toggle access
  const handleToggleAccess = useCallback(
    async (group: IEndpointGroup) => {
      if (!selectedOrgId) return;

      const orgHasAccess = hasAccess(selectedOrgId, group.id);
      let success: boolean;

      if (orgHasAccess) {
        success = await removeAccess(selectedOrgId, group.id);
        if (success) {
          setSuccessMessage(`Tilgang til "${group.name}" er fjernet`);
        } else {
          setErrorMessage(`Kunne ikke fjerne tilgang til "${group.name}"`);
        }
      } else {
        success = await addAccess(selectedOrgId, group.id);
        if (success) {
          setSuccessMessage(`Tilgang til "${group.name}" er lagt til`);
        } else {
          setErrorMessage(`Kunne ikke legge til tilgang til "${group.name}"`);
        }
      }
    },
    [selectedOrgId, hasAccess, addAccess, removeAccess],
  );

  // Render endpoint group item
  const renderGroupItem = useCallback(
    (group: IEndpointGroup) => {
      const orgHasAccess = selectedOrgId
        ? hasAccess(selectedOrgId, group.id)
        : false;

      return (
        <div
          key={group.id}
          className={styles.groupItem}
          data-testid={`${testId}-group-${group.id}`}
        >
          <div className={styles.groupInfo}>
            <div className={styles.groupName}>{group.name}</div>
            <div className={styles.groupDescription}>{group.description}</div>
          </div>
          <IconButton
            icon={orgHasAccess ? <X size={16} /> : <Plus size={16} />}
            onClick={() => handleToggleAccess(group)}
            disabled={isUpdating || !selectedOrgId}
            aria-label={
              orgHasAccess
                ? `Fjern tilgang til ${group.name}`
                : `Legg til tilgang til ${group.name}`
            }
            size="sm"
            shape="square"
            className={classNames(
              styles.toggleButton,
              orgHasAccess ? styles.removeButton : styles.addButton,
            )}
            data-testid={`${testId}-toggle-${group.id}`}
          />
        </div>
      );
    },
    [selectedOrgId, hasAccess, handleToggleAccess, isUpdating, testId],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>
        Kontroller hvilke API-funksjoner hver organisasjon har tilgang til.
        Dette styrer hvilke handlinger brukere kan utføre i systemet.
      </p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      <div className={layoutStyles.card}>
        <SectionHeader as="h2" size="md">
          Endpoint-grupper per organisasjon
        </SectionHeader>

        {/* Organization selector */}
        <div className={styles.selectorWrapper}>
          <div className={styles.formField}>
            <label htmlFor="endpoints-org-select" className={styles.fieldLabel}>
              Velg organisasjon
            </label>
            <Select<IOrganization>
              id="endpoints-org-select"
              appearance="clean"
              placeholder="Søk etter organisasjon..."
              size="md"
              options={orgOptions}
              value={selectedOrg}
              onChange={setSelectedOrg}
              searchable
              disabled={isLoading}
              renderOption={renderOrgOption}
              data-testid={`${testId}-org-select`}
            />
          </div>
        </div>

        <StatusMessages
          error={error}
          isLoading={isLoading}
          errorLabel="Kunne ikke laste endpoint-grupper"
          loadingLabel="Laster endpoint-grupper..."
          testId={testId}
        />

        {/* Endpoint groups list */}
        {!isLoading && !error && (
          <>
            {selectedOrgId ? (
              <FilteredList
                items={endpointGroups}
                filterItem={(group, normalized) => {
                  const searchFields = [group.name, group.description];
                  return searchFields.some((value) =>
                    value.toLowerCase().includes(normalized),
                  );
                }}
                renderItem={renderGroupItem}
                placeholder="Søk etter endpoint-gruppe"
                debounce={150}
                className={styles.listWrapper}
                emptyState={
                  <div className={styles.emptyState}>
                    Ingen endpoint-grupper funnet
                  </div>
                }
                data-testid={`${testId}-list`}
              />
            ) : (
              <div className={styles.noOrgSelected}>
                Velg en organisasjon for å administrere endpoint-grupper
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

EndpointGroupsSection.displayName = "EndpointGroupsSection";

export default EndpointGroupsSection;
